import { AxiosInstance } from 'axios';
import {
  CampaignBidPriceSetting,
  ChannelGroupBrand,
  ChannelGroupDTO,
  ChannelGroupFormData,
  ChannelGroupListDTO,
  ChannelGroupStatusFilter,
  ChannelType,
  DynamicCampaignAdTypeSetting
} from 'core/channelGroup/ChannelGroup';
import { defaultTo, get, isNil, omit } from 'lodash';
import client from './RestClient';
import { Account, CompanyMember, Currency } from 'core';
import { CreativeSize } from 'core/creative/Creative';
import {
  ChannelGroupSetting,
  CreativeTypeSetting
} from 'core/channelGroup/ChannelGroupSettings';
import {
  CloudStorageManager,
  DefaultCloudStorageManager
} from 'core/cloudStorage/CloudStorageManager';
import i18n from 'i18n';
import { withSalesChannelArchivedSuffix } from 'utils/I18nUtils';
import { SUPPORTED_LANGUAGES } from 'utils/LanguageCode';
import { buildBrandPayload } from 'core/channelGroup/channelGroupBrand';

export interface ChannelGroupWebService {
  getChannelGroups(
    useI18nName?: boolean,
    status?: ChannelGroupStatusFilter
  ): Promise<ChannelGroupListDTO[]>;
  getChannelGroup(id: number): Promise<ChannelGroupDTO>;
  createChannelGroup(formData: ChannelGroupFormData): Promise<ChannelGroupDTO>;
  editChannelGroup(
    id: number,
    formData: ChannelGroupFormData
  ): Promise<ChannelGroupDTO>;
  addChannelGroupMember(
    id: number,
    email: string,
    roleName: string
  ): Promise<void>;
  deleteChannelGroupMember(
    id: number,
    email: string,
    roleName: string
  ): Promise<void>;
  getChannelGroupMembers(id: number): Promise<CompanyMember[]>;
  getOutdoorCreativeSizes(id: number): Promise<CreativeSize[]>;
  getCampaignAdTypesUsageStatus(
    adTypeIds: number[]
  ): Promise<Record<number, boolean>>;
  getAllowedSalesChannels(): Promise<SelectOptions[]>;
  getReportChannels(): Promise<SelectOptions[]>;
  archiveChannelGroup(id: number, reason: string): Promise<ChannelGroupDTO>;
  unarchiveChannelGroup(id: number): Promise<ChannelGroupDTO>;
  getSimulcastGroups(id: number, isTesting?: boolean): Promise<SelectOptions[]>;
  getSpaceIdsOfSimulcastGroup(
    groupId: string,
    isTesting?: boolean
  ): Promise<string[]>;
}
export class RestfulChannelGroupWebService implements ChannelGroupWebService {
  restClient: AxiosInstance;

  constructor(
    restClient: AxiosInstance = client,
    private cloudStorageManager: CloudStorageManager = new DefaultCloudStorageManager()
  ) {
    this.restClient = restClient;
  }

  wrapChannelGroup<T extends { channelId: number; name: string }>(
    json: T,
    useI18nName = false
  ) {
    const channelId = json.channelId;
    // ⚠️ `?? []` 不能省成 `get(..., [])`：`get` 的預設值只在欄位是 undefined 時生效，
    // **null 會原樣回傳**。後端對「沒設定廣告類型」與「非 dynamic」的渠道
    // 這個欄位就是 null，直接 `.map()` 會 throw ——
    // 而這條路徑是登入時的 `ChannelGroupSetting.init`，炸掉等於整個 app 開不起來。
    const campaignAdTypeSettings = (
      (get(json, 'campaignAdTypeSettings') ??
        []) as DynamicCampaignAdTypeSetting[]
    ).map((setting) => {
      const nameI18n = setting.nameI18n || {};
      const name = useI18nName
        ? nameI18n[i18n.language] || setting.name
        : setting.name;
      return {
        adTypeId: setting.adTypeId,
        state: setting.state,
        name,
        pricingModel: setting.pricingModel,
        channelGroupId: channelId,
        bannerTypes: defaultTo(setting.bannerTypes, []).map(
          (banenrType: CreativeTypeSetting) => ({
            ...banenrType,
            name,
            channelGroupId: channelId
          })
        ),
        nameI18n: setting.nameI18n,
        screenDisplayType: setting.screenDisplayType
      };
    });
    const campaignAdTypeSettingsIdNameMap = campaignAdTypeSettings.reduce(
      (acc, setting) => {
        if (setting.adTypeId) {
          acc[setting.adTypeId] = setting.name;
        }
        return acc;
      },
      {}
    );
    const campaignBidPrice = (
      get(
        json,
        'property.addonProps.campaignBidPrice',
        []
      ) as CampaignBidPriceSetting[]
    ).map((bidPriceSetting) => {
      return {
        ...bidPriceSetting,
        type: bidPriceSetting.adTypeId
          ? campaignAdTypeSettingsIdNameMap[bidPriceSetting.adTypeId]
          : bidPriceSetting.type
      };
    });
    const nameI18n = get(json, 'nameI18n') ?? {};
    // 把渠道名稱灌成 runtime i18n resource，全站顯示渠道名都讀這一份。
    //
    // 「(停用)」後綴的**唯一產生者就是這裡** —— 後端送來的 `name` / `nameI18n`
    // 永遠是原名。這是全站唯一同時知道「渠道 id、各語系名稱、停用狀態」三者的地方，
    // 所以後綴加在這裡。
    //
    // ⚠️ 回傳的 DTO `name` / `nameI18n` **一律維持原名**：渠道管理的編輯表單有三個
    // 輸入框直接綁 `nameI18n.en / .zh-TW / .ja`，使用者一存檔就整包 PUT 回去。
    // 後綴一旦混進那些欄位就會被當成名字的一部分寫進 DB，之後拿不掉，
    // 渠道復原成啟用之後也會一直留著。
    //
    // ⚠️ 這裡**不能**走 `ChannelGroupSetting` —— 這段跑在 `register()` 之前，
    // registry 裡還沒有這個渠道，只能直接讀 json。
    // 也**不要**在這裡自己組 `status === ARCHIVED || pmaxArchived`：合併判定只有後端一份。
    const isEffectivelyArchived = !!get(json, 'effectiveArchived');
    const key = `salesChannels.${channelId}`;
    SUPPORTED_LANGUAGES.forEach((lng) => {
      // 後端不保證 nameI18n 有值（很多渠道是 null），退回單語 `name`。
      const baseName = nameI18n[lng] || json.name;
      i18n.addResource(
        lng,
        'translation',
        key,
        isEffectivelyArchived
          ? withSalesChannelArchivedSuffix(baseName, lng)
          : baseName
      );
    });
    const screenPixelWidth = get(json, 'screenProperty.screenPixelWidth');
    const screenPixelHeight = get(json, 'screenProperty.screenPixelHeight');
    return {
      ...omit(json, 'channelId', 'name'),
      id: channelId,
      channel: json.name,
      name: useI18nName ? nameI18n[i18n.language] || json.name : json.name,
      // ECOPD-5470: 後端把 allowedVideoCodecs 放在 property.addonProps,前端拉到頂層供 detail / 素材頁使用
      allowedVideoCodecs: get(
        json,
        'property.addonProps.allowedVideoCodecs',
        []
      ),
      alertEmails: get(json, 'alertEmails', []),
      property: {
        // ECOPD-5581: property 在這裡是整個重建的，不是原樣帶過——所以
        // addonProps 以外的兄弟節點（epaperProps）必須先展開，否則會被靜靜丟掉。
        // 這跟頂層 `...omit(json, 'channelId', 'name')` 的處理方式一致。
        ...get(json, 'property', {}),
        addonProps: {
          ...get(json, 'property.addonProps', {}),
          currency: get(json, 'property.addonProps.currency', Currency.NTD),
          campaignBidPrice,
          defaultMargin: get(json, 'property.addonProps.defaultMargin', 0),
          outdoorCreativeSizes:
            screenPixelWidth && screenPixelHeight
              ? [
                  {
                    width: screenPixelWidth,
                    height: screenPixelHeight
                  }
                ]
              : [],
          nonExpressPassCategories: get(
            json,
            'property.addonProps.nonExpressPassCategories',
            []
          )
        }
      },
      campaignAdTypeSettings
    };
  }

  wrapAccount(json: any): Partial<Account> {
    return {
      id: get(json, 'accountId', get(json, 'account_id')),
      name: get(json, 'name'),
      email: get(json, 'email'),
      language: get(json, 'language', 'zh_TW'),
      activated: get(json, 'activated', true),
      status: get(json, 'status')
    };
  }

  // ECOPD-5566: 停用（ARCHIVED）的渠道**照樣回傳**，不在這裡一刀切。
  // 停用渠道底下的既有訂單 / 群組 / 活動 / 素材仍要看得到，而 FE 幾乎每條讀取路徑
  // 都會 `ChannelGroupSetting.fromId(channelGroupId)` —— 濾在這裡會讓那些頁面直接 throw。
  // 「使用者可以挑選的清單」改在各出口用 `ChannelGroupSetting.activeValues()` 過濾。
  //
  // ECOPD-5701: `status` 省略 = 要全部。四個呼叫端裡只有渠道管理列表會帶它，
  // 其餘三個（registry 的 init/refresh、`AgencyManager` 的 bid price 差異比對）
  // **必須拿到全部渠道**。⚠️ 不要為了「安全」把預設改成 ACTIVE：停用渠道會從
  // registry 消失、`ChannelGroupSetting.fromId()` 開始 throw，而且不會有測試變紅。
  // 見 `.claude/context/adr/0003-channel-list-status-filter-defaults-to-all.md`。
  async getChannelGroups(
    useI18nName?: boolean,
    status?: ChannelGroupStatusFilter
  ) {
    const response = await this.restClient.get(
      status ? `/channel-groups?status=${status}` : `/channel-groups`
    );
    return get(response, 'data.records', []).map((record) =>
      this.wrapChannelGroup(record, useI18nName)
    ) as ChannelGroupListDTO[];
  }

  async getChannelGroup(id: number) {
    const response = await this.restClient.get(`/channel-groups/${id}`);
    return this.wrapChannelGroup(response.data) as ChannelGroupDTO;
  }

  /**
   * ECOPD-5581: 電子紙預設圖片的檔案直傳 cloud storage，payload 只帶 URL 字串
   * ——與經銷商 logo 同一套流程，不是 multipart。後端拿到 URL 之後才會去元太圖庫
   * 換 image UUID。
   *
   * 只有電子紙渠道會帶這個欄位；其他渠道類型一律回 undefined，讓它不要進 payload。
   */
  private async resolveEpaperDefaultImageUrl(
    formData: ChannelGroupFormData
  ): Promise<string | undefined> {
    if (formData.channelType !== ChannelType.E_PAPER) {
      return undefined;
    }
    const imageData = formData.epaperDefaultImageData;
    if (!imageData) {
      return undefined;
    }
    // 沒換圖就沿用原本的 URL，不要重傳一份一模一樣的檔案上去
    return imageData.file
      ? this.cloudStorageManager.uploadFileToCloud(imageData.file)
      : imageData.url;
  }

  private async resolveImageUrl(
    imageData: ImageFieldData | undefined,
    currentUrl: string | null | undefined
  ): Promise<string | undefined> {
    // 表單沒有這個欄位（例如呼叫端沒經過渠道編輯表單）就沿用原本的 URL，不要因此清掉
    if (!imageData) {
      return currentUrl ?? undefined;
    }
    return imageData.file
      ? this.cloudStorageManager.uploadFileToCloud(imageData.file)
      : imageData.url;
  }

  /**
   * 品牌的兩張地圖圖示，與電子紙預設圖同一套：檔案直傳 cloud storage 的 tmp，
   * payload 只帶 URL，後端驗證格式後再搬到渠道自己的目錄。
   *
   * 不分渠道類型：非戶外渠道的表單不顯示這兩個欄位，值就是原本的 URL，原樣送回。
   */
  private async resolveBrandIconUrls(
    formData: ChannelGroupFormData
  ): Promise<Pick<ChannelGroupBrand, 'filteredIconUrl' | 'normalIconUrl'>> {
    const brand = formData.property.addonProps.brand;
    const [filteredIconUrl, normalIconUrl] = await Promise.all([
      this.resolveImageUrl(
        formData.brandFilteredIconData,
        brand?.filteredIconUrl
      ),
      this.resolveImageUrl(formData.brandNormalIconData, brand?.normalIconUrl)
    ]);
    return { filteredIconUrl, normalIconUrl };
  }

  async createChannelGroup(formData: ChannelGroupFormData) {
    const epaperDefaultImageUrl =
      await this.resolveEpaperDefaultImageUrl(formData);
    const brandIconUrls = await this.resolveBrandIconUrls(formData);
    const response = await this.restClient.post(
      `/channel-groups`,
      this.wrapForServer(formData, epaperDefaultImageUrl, brandIconUrls)
    );
    return this.wrapChannelGroup(response.data) as ChannelGroupDTO;
  }

  async editChannelGroup(id: number, formData: ChannelGroupFormData) {
    const epaperDefaultImageUrl =
      await this.resolveEpaperDefaultImageUrl(formData);
    const brandIconUrls = await this.resolveBrandIconUrls(formData);
    const response = await this.restClient.put(`/channel-groups`, {
      channelId: id,
      ...this.wrapForServer(formData, epaperDefaultImageUrl, brandIconUrls)
    });
    return this.wrapChannelGroup(response.data) as ChannelGroupDTO;
  }

  /**
   * ECOPD-5572：PMax 系統管理者自主停用渠道。
   *
   * 端點語彙用 `archive`（兩個 repo 的渠道領域詞彙一直都是 archive），
   * **UI 上的中文字則是「停用」** —— 這個不一致是刻意的。
   *
   * ⚠️ 這支會在後端觸發破壞性連鎖：暫停底下啟用中的活動、停用 PMP 訂單，
   * 並且**刪除 RMax 端對應的 deal**，而且 unarchive 不會恢復任何一項。
   * 呼叫端一定要先讓使用者確認。
   *
   * 回應走 `wrapChannelGroup`：它會順便重灌 `salesChannels.<id>` 這個 runtime
   * i18n resource，所以停用成功後全站的渠道名後綴自動就對了。
   *
   * ECOPD-5701：`reason` 必填，後端對空值、純空白、超過 500 字回 400，
   * 呼叫端要先 trim 並擋掉空值。
   *
   * ⚠️ 已自停的渠道再打一次這支，後端什麼都不做（不覆寫理由、不寫 log、不重跑連鎖）。
   * 改理由的唯一流程是 unarchive → archive。前端沒有這個入口，也走不到
   * —— `pmaxArchived=true` 時畫面只渲染「重新啟用」。
   */
  async archiveChannelGroup(id: number, reason: string) {
    const response = await this.restClient.put(
      `/channel-groups/${id}/archive`,
      { reason }
    );
    return this.wrapChannelGroup(response.data) as ChannelGroupDTO;
  }

  /**
   * ECOPD-5572：解除 PMax 自停。
   *
   * **不做任何恢復** —— 被停掉的活動與 PMP 訂單維持停用，由使用者自行重新開啟；
   * RMax 端的 deal 在 archive 時已被刪除，這裡也救不回來。
   * 若該渠道同時被 RMax 封存，解除自停後 `effectiveArchived` 仍會是 true。
   */
  async unarchiveChannelGroup(id: number) {
    const response = await this.restClient.put(
      `/channel-groups/${id}/unarchive`
    );
    return this.wrapChannelGroup(response.data) as ChannelGroupDTO;
  }

  async addChannelGroupMember(
    id: number,
    email: string,
    roleName: string
  ): Promise<void> {
    await this.restClient.post(`/channel-groups/${id}/members`, {
      email,
      roleName: `ROLE_${roleName}`
    });
  }

  async deleteChannelGroupMember(
    id: number,
    email: string,
    roleName: string
  ): Promise<void> {
    await this.restClient.delete(`/channel-groups/${id}/members`, {
      data: {
        email,
        roleName: `ROLE_${roleName}`
      }
    });
  }

  async getChannelGroupMembers(id: number) {
    const response = await this.restClient.get(`/channel-groups/${id}/members`);
    return response.data.records.map((record) => ({
      ...record,
      account: this.wrapAccount(record.account)
    }));
  }

  async getOutdoorCreativeSizes(id: number) {
    const response = await this.restClient.get(
      `/channel-groups/${id}/outdoor-creative-sizes`
    );
    return response.data.records as CreativeSize[];
  }

  wrapForServer(
    formData: ChannelGroupFormData,
    epaperDefaultImageUrl?: string,
    brandIconUrls?: Pick<ChannelGroupBrand, 'filteredIconUrl' | 'normalIconUrl'>
  ) {
    const campaignBidPrice = formData.property.addonProps.campaignBidPrice;
    const brand = formData.property.addonProps.brand;
    return {
      // ECOPD-5581: epaperDefaultImageData 是純前端的 { file, url }，送出前已經換成
      // property.epaperProps.epaperDefaultImageUrl，本身不該進 payload。
      // （channelType 不在此列：它只宣告在後端的 ReadDTO，PUT 綁的 UpdateDTO 上沒有
      //   這個屬性，送了也綁不進去。拿掉反而是本單範圍外的 payload 形狀變更。）
      //
      // ECOPD-5572：自停旗標**不得**經由渠道設定表單送回後端。
      // `ChannelGroupFormData` 型別上已經 Omit 掉它們，這裡是第二層 ——
      // 兩層都擋是刻意的：型別擋編譯期，這裡擋 `as any` 之類的繞過。後端也會忽略。
      ...omit(
        formData,
        'screenProperty',
        'epaperDefaultImageData',
        'brandFilteredIconData',
        'brandNormalIconData',
        'pmaxArchived',
        'effectiveArchived',
        // ECOPD-5701：理由同理。
        'pmaxArchiveReason',
        'rmaxArchiveReason'
      ),
      property: {
        // ECOPD-5581: 表單裡那組 epaperProps 一律不帶出去，改在下面重建。
        // 它從 GET 帶進來時含唯讀的 siteId（值只由 RMax 同步寫入），而畫面上
        // 不給編輯擋不住——Formik 的 initialValues 會被原樣送回去。後端另有一道
        // 白名單不複製 siteId，這裡是雙保險的第一道。
        ...omit(formData.property, 'epaperProps'),
        // 可寫的只有預設圖片 URL。非電子紙渠道 resolve 回 undefined，整組就不帶，
        // 避免 payload 多出一組對它沒有意義的欄位。
        ...(epaperDefaultImageUrl === undefined
          ? {}
          : { epaperProps: { epaperDefaultImageUrl } }),
        addonProps: {
          ...formData.property.addonProps,
          campaignBidPrice: campaignBidPrice.map((value) => {
            if (!isNil(value.adTypeId) || !isNil(value.tmpId)) {
              return omit(value, 'type');
            }
            return value;
          }),
          // 後端的寫入是整包覆蓋，沒帶 brand 等於清空，所以沒動到的欄位也要原值送回
          brand: buildBrandPayload(
            brand,
            brandIconUrls ?? {
              filteredIconUrl: brand?.filteredIconUrl,
              normalIconUrl: brand?.normalIconUrl
            }
          )
        }
      }
    };
  }

  async getCampaignAdTypesUsageStatus(
    adTypeIds: number[]
  ): Promise<Record<number, boolean>> {
    const response = await this.restClient.get(
      `/v2/campaign-ad-types/usage-status?ids=${adTypeIds.join(',')}`
    );
    return response.data;
  }

  async getAllowedSalesChannels(): Promise<SelectOptions[]> {
    const response = await this.restClient.get(
      '/channel-groups/available-channels'
    );
    return response.data.records
      .map((record: SelectOptions) => ({
        ...record,
        label: record.label.replace(/_/g, ' ')
      }))
      .sort((a: SelectOptions, b: SelectOptions) => +a.value - +b.value);
  }

  // ECOPD-5566：報表專用的渠道清單，**含停用渠道** ——
  // 停用渠道的歷史報表仍要查得到，下拉選不到就等於看不到。
  //
  // 與 `getAllowedSalesChannels`（建立流程用、排除停用渠道）是兩支不同的端點，
  // 不要合併：兩者對「停用渠道要不要出現」的要求正好相反。
  //
  // ⚠️ label 由後端做 i18n，但**不含停用後綴** —— 後綴一律由前端加。
  // 這支的 payload 沒有 status，所以用 `value`（= channelId）查本地 registry；
  // registry 在登入時就整包載入且含停用渠道，不需要額外請求。
  async getReportChannels(): Promise<SelectOptions[]> {
    const response = await this.restClient.get(
      '/channel-groups/report-channels'
    );
    return get(response, 'data.records', [])
      .map((record: SelectOptions) => ({
        ...record,
        label: ChannelGroupSetting.isEffectivelyArchivedChannel(record.value)
          ? withSalesChannelArchivedSuffix(record.label)
          : record.label
      }))
      .sort((a: SelectOptions, b: SelectOptions) => +a.value - +b.value);
  }

  async getSimulcastGroups(
    id: number,
    isTesting?: boolean
  ): Promise<SelectOptions[]> {
    let path = `/channel-groups/${id}/simulcast-groups`;
    if (isTesting) {
      path += '?status=TESTING';
    }
    const response = await this.restClient.get(path);
    return response.data.records;
  }

  async getSpaceIdsOfSimulcastGroup(
    simulcastGroupId: string,
    isTesting?: boolean
  ): Promise<string[]> {
    let path = `/channel-groups/simulcast-groups/${simulcastGroupId}/space-ids`;
    if (isTesting) {
      // POS-8073：這一支 BE 的參數名是 `spaceStatus`（ECOPD-4964 從 `status` 改名，
      // 前端當時沒跟上）。名字不對會被 Spring 無聲忽略、回到預設的 ON 版位。
      // ⚠️ 上面 getSimulcastGroups 那一支的參數名確實是 `status`，兩者不同、別對齊。
      path += '?spaceStatus=TESTING';
    }
    const response = await this.restClient.get(path);
    return response.data.records;
  }
}
